import dayjs from "dayjs";

import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  faFileCircleExclamation,
  faFileCode,
  faFileLines,
  faFilePdf,
  faFileZipper,
  faImage,
  faMusic,
} from "@fortawesome/free-solid-svg-icons";
import ts, { transpile } from "typescript";

export const getSystemTheme = () =>
  window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";

export const getItemFromStore = (
  key: string,
  defaultValue?: string | boolean,
  store = localStorage
) => {
  try {
    return store.getItem(key) === null
      ? defaultValue
      : JSON.parse(store.getItem(key) as string);
  } catch {
    return store.getItem(key) || defaultValue;
  }
};

export const cleanText = (string: string) =>
  (string.charAt(0).toUpperCase() + string.slice(1))
    .replace(/-/g, " ")
    .replace("_and_", "&");

export const setItemToStore = (
  key: string,
  payload: string,
  store = localStorage
) => store.setItem(key, payload);

export const capitalize = (string: string) =>
  (string.charAt(0).toUpperCase() + string.slice(1))
    .replace(/-/g, " ")
    .replace("_and_", "&");

export const snakeCase = (string: string) => {
  return string
    .replace(/\W+/g, " ")
    .split(/ |\B(?=[A-Z])/)
    .map((word) => word.toLowerCase())
    .join("_");
};

export const getColor = (name: string) => {
  const dom = document.documentElement;
  return getComputedStyle(dom).getPropertyValue(`--phoenix-${name}`).trim();
};

/* get Dates between */
export const getDates = (
  startDate: Date,
  endDate: Date,
  interval: number = 1000 * 60 * 60 * 24
): Date[] => {
  const duration = +endDate - +startDate;
  const steps = duration / interval;
  return Array.from(
    { length: steps + 1 },
    (v, i) => new Date(startDate.valueOf() + interval * i)
  );
};

export const getPastDates = (
  duration: "week" | "month" | "year" | number
): Date[] => {
  let days;

  switch (duration) {
    case "week":
      days = 7;
      break;
    case "month":
      days = 30;
      break;
    case "year":
      days = 365;
      break;

    default:
      days = duration;
  }

  const date = new Date();
  const endDate = date;
  const startDate = new Date(new Date().setDate(date.getDate() - (days - 1)));
  return getDates(startDate, endDate);
};

export const currencyFormat = (
  amount: number,
  options: Intl.NumberFormatOptions = {}
) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "usd",
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
    ...options,
  }).format(amount);
};

export const getNumbersInRange = (startAt: number, endAt: number) => {
  return [...Array(endAt + 1 - startAt).keys()].map((i) => i + startAt);
};

export const numberFormat = (
  number: number,
  notation: "standard" | "compact" = "standard"
) =>
  new Intl.NumberFormat("en-US", {
    notation,
  }).format(number);

/* Get Random Number */
export const getRandomNumber = (min: number, max: number) => {
  return Math.floor(Math.random() * (max - min) + min);
};

export const getFileIcon = (fileFormat: string): IconProp => {
  switch (fileFormat) {
    case "zip":
    case "rar":
      return faFileZipper;
    case "bat":
      return faFileCode;
    case "txt":
      return faFileLines;
    case "mad":
      return faFileCircleExclamation;
    case "wav":
      return faMusic;
    case "pdf":
      return faFilePdf;
    case "jpg":
    case "png":
    case "jpeg":
      return faImage;
    default:
      return faFileLines;
  }
};

export const getIntegerArrayBetween = (start = 0, end: number): number[] =>
  new Array(end + 1 - start).fill(1).map((_, i) => i + start);

export const parseData = (data: string) => {
  try {
    return JSON.parse(data);
  } catch {
    return data;
  }
};

export const hexToRgb = (hex: string) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return [r, g, b];
};

export const rgbaColor = (color = "#fff", alpha = 0.5) =>
  `rgba(${hexToRgb(color)}, ${alpha})`;

export const transformTSCode = (
  snippet: string,
  target: ts.ScriptTarget = ts.ScriptTarget.ES2015
) =>
  transpile(snippet, {
    jsx: ts.JsxEmit.React,
    target,
  });

export const getFileExtension = (fileName: string, separator = ".") =>
  fileName.split(separator).pop() || "unknown";

export const isImageFile = (file: File) => {
  const imageMimeTypes = [
    "image/jpeg",
    "image/png",
    "image/gif",
    "image/bmp",
    "image/webp",
  ];
  return imageMimeTypes.includes(file.type);
};

export const convertFileToAttachment = (file: File) => ({
  name: file.name,
  size: `${(file.size / 1024).toFixed(2)} KB`,
  format: getFileExtension(file.name),
  preview: isImageFile(file) ? URL.createObjectURL(file) : undefined,
});

export const getProgressColorVariant = (value: number) => {
  if (value < 21) {
    return "warning";
  } else if (value < 41) {
    return "info";
  } else if (value < 81) {
    return "primary";
  } else {
    return "success";
  }
};

export const getPriorityColor = (priority: string) => {
  switch (priority.toLowerCase()) {
    case "urgent":
      return "danger";
    case "high":
      return "warning";
    case "medium":
      return "success";
    case "low":
      return "info";
    default:
      return "primary";
  }
};

export const invertHex = (hex: string) =>
  `#${(Number(`0x1${hex.slice(1)}`) ^ 0xffffff)
    .toString(16)
    .substring(1)
    .toUpperCase()}`;

export const getColorByBgColor = (bgColor: string) => {
  const rgba = hexToRgb(bgColor);
  if (rgba[0] * 0.299 + rgba[1] * 0.587 + rgba[2] * 0.114 > 186) {
    return "black";
  } else {
    return "white";
  }
};

export const urlToFile = async (url: string) => {
  const filename = url.substring(url.lastIndexOf("/") + 1);

  // Extract the file extension and determine the MIME type
  const extension = filename.split(".").pop();
  const mimeType =
    extension === "png"
      ? "image/png"
      : extension === "jpg" || extension === "jpeg"
        ? "image/jpeg"
        : extension === "gif"
          ? "image/gif"
          : "application/octet-stream"; // Fallback for unknown types

  const response = await fetch(url);
  const blob = await response.blob();
  return new File([blob], filename, { type: mimeType });
};

export const formatDateToTime = (date: Date) => {
  const formatter = new Intl.DateTimeFormat("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  });
  return formatter.format(date);
};

export const dateFormatter = (
  timestamp: string | number | Date,
  showTime: boolean = true
): string => {
  if (!timestamp || !dayjs(timestamp).isValid()) {
    return "Invalid date";
  }

  const formatString = showTime ? "MMM DD, hh:mm A" : "MMM DD";
  return dayjs(timestamp).format(formatString);
};

export const dateFormatterForEmail = (timestamp: string | number | Date): string => {
  if (!timestamp || !dayjs(timestamp).isValid()) {
    return "Invalid date";
  }

  return dayjs(timestamp).format("MMM DD YYYY, hh:mm A");
};

export const getInitials = (firstName: any, lastName: any) => {
  const firstInitial = firstName?.[0]?.toUpperCase() || "";
  const lastInitial = lastName?.[0]?.toUpperCase() || "";
  return `${firstInitial}${lastInitial}`;
};

export const convertToISO8601 = (datetimeValue: string) => {
  if (!datetimeValue) return "";
  return new Date(datetimeValue).toISOString();
};

export const formatFileSize = (bytes: any) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};

export const formatDate = function formatDate(
  isoString: string,
  locale: string = "en-GB"
): string {
  if (isoString === null) {
    return "N/A";
  }
  const date = new Date(isoString);

  return date.toLocaleDateString(locale, {
    year: "numeric",
    month: "long",
    day: "2-digit",
  });
};

export const getISODate = (date: Date) => {
  try {
    return date ? new Date(date).toISOString() : null;
  } catch {
    return null; // or handle the error as needed
  }
};

export const compareDateWithToday = (date: any) => {
  const isoDate = dayjs(date);
  const today = dayjs().startOf("day"); // Remove time part for accurate comparison

  if (isoDate.isAfter(today)) {
    return "Ahead";
  } else if (isoDate.isBefore(today)) {
    return "Due";
  } else {
    return "Same Date";
  }
};

// export function extractNameFromURL(url: any) {
//   const parts = url.split("/documents/");
//   if (parts.length > 1) {
//     return parts[1]; // Get the part after '/documents/'
//   }
//   return null;
// }

export function extractNameFromURL(url: any) {
  try {
    const parts = url.split("/documents/");
    if (parts.length > 1) {
      return decodeURIComponent(parts[1]); // Decode special characters
    }
    return null;
  } catch (error) {
    console.error("Error decoding URL:", error);
    return null;
  }
}

export const entityTypes = {
  lead: "lead",
  deal: "deal",
  buyer: "buyer",
  general: "general",
};

/**
 * Safely formats error objects for React rendering
 * @param error - The error object or string to format
 * @returns A string representation of the error
 */
export const formatErrorForReact = (error: any): string => {
  if (typeof error === 'string') {
    return error;
  }
  
  if (typeof error === 'object' && error !== null) {
    // Handle common error object structures
    if (error.message && typeof error.message === 'string') {
      return error.message;
    }
    
    if (error.statusCode && typeof error.statusCode === 'number') {
      return `Error ${error.statusCode}${error.status ? `: ${error.status}` : ''}`;
    }
    
    if (error.code && typeof error.code === 'string') {
      return `Error: ${error.code}`;
    }
    
    if (error.name && typeof error.name === 'string') {
      return `Error: ${error.name}`;
    }
    
    // Fallback to JSON string for unknown objects (but truncated)
    try {
      const jsonStr = JSON.stringify(error);
      if (jsonStr.length > 100) {
        return 'An error occurred (details in console)';
      }
      return jsonStr;
    } catch {
      return 'An error occurred';
    }
  }
  
  return 'An error occurred';
};

/**
 * Format commission value with proper decimal places
 * @param commission - The commission value (number or string)
 * @param commissionType - The type of commission ('percentage' or 'flat')
 * @returns Formatted commission string
 */
export const formatCommission = (commission: number | string | null | undefined, commissionType: string = 'percentage'): string => {
  if (!commission && commission !== 0) return 'N/A';
  
  const numericCommission = parseFloat(commission.toString());
  if (isNaN(numericCommission)) return 'N/A';
  
  // Format to 2 decimal places and remove trailing zeros
  const formattedValue = numericCommission.toFixed(2).replace(/\.?0+$/, '');
  
  // Add percentage symbol for percentage types, nothing for flat
  const suffix = commissionType === 'flat' ? '' : '%';
  
  return `${formattedValue}${suffix}`;
};
